import { createContext, useContext } from 'react';

import RetryPaymentApiAdapter from '@adapters/retryApiAdapter';
import { ThunkDispatch, thunkDispatchWithDependencies } from '@dispatch';

import { useAppContext } from './AppContextProvider';
import { useConfig } from './ConfigProvider';
import { useRetryState } from './RetryDataProvider';

type ThunkDispatchData = {
  thunkDispatch: ThunkDispatch;
};

const ThunkDispatchContext = createContext<ThunkDispatchData>({
  thunkDispatch: () => null,
});

export const useThunkDispatch = () => useContext(ThunkDispatchContext);

const ThunkDispatchProvider: React.FC = ({ children }) => {
  const {
    config: { retryPaymentBaseUrl },
  } = useConfig();
  const {
    retrySuccessWithData,
    initiateRetryPaymentFailure,
    initiateRetryPaymentStart,
    initiateRetryPaymentSuccess,
    processPaymentAuthorisationFailure,
    processPaymentAuthorisationStart,
    processPaymentAuthorisationSuccess,
  } = useRetryState();
  const { domain, locale } = useAppContext();
  const retryApi = new RetryPaymentApiAdapter(retryPaymentBaseUrl, domain, locale);
  const dependencies = {
    retryApi,
    retrySuccessWithData,
    initiateRetryPaymentFailure,
    initiateRetryPaymentStart,
    initiateRetryPaymentSuccess,
    processPaymentAuthorisationFailure,
    processPaymentAuthorisationStart,
    processPaymentAuthorisationSuccess,
  };
  const thunkDispatch = thunkDispatchWithDependencies(dependencies);
  return (
    <ThunkDispatchContext.Provider value={{ thunkDispatch }}>
      {children}
    </ThunkDispatchContext.Provider>
  );
};

export default ThunkDispatchProvider;
